import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { API_BASE } from '../utils/api';
import { pluginSeo } from '../utils/seo';

function ethereumWeiAmount(amount) {
const numericAmount = Number(amount);
if (!Number.isFinite(numericAmount) || numericAmount <= 0) return '';
const [whole, fraction = ''] = numericAmount.toFixed(18).split('.');
return `${whole}${fraction.padEnd(18, '0')}`.replace(/^0+(?=\d)/, '');
}

function buildCryptoPaymentUri(currency, address, amount) {
const normalizedCurrency = String(currency || '').trim().toLowerCase();
const normalizedAddress = String(address || '').trim();
if (!normalizedAddress) return '';
if (normalizedCurrency === 'btc') {
  return `bitcoin:${normalizedAddress}?amount=${encodeURIComponent(String(amount))}`;
}
if (normalizedCurrency === 'eth') {
  const wei = ethereumWeiAmount(amount);
  return `ethereum:${normalizedAddress}${wei ? `?value=${wei}` : ''}`;
}
return `${normalizedCurrency}:${normalizedAddress}?amount=${encodeURIComponent(String(amount))}`;
}

const PAYMENT_PROGRESS = {
waiting: { percent: 10, label: 'Awaiting payment', detail: 'Send the exact amount below. We will detect the transfer automatically.' },
partially_paid: { percent: 25, label: 'Partial payment received', detail: 'The amount received is below the requested total. Check your wallet transfer amount.' },
confirming: { percent: 45, label: 'Payment detected', detail: 'Your transfer was found and is waiting for blockchain confirmations.' },
confirmed: { percent: 70, label: 'Blockchain confirmed', detail: 'Your transfer is confirmed. NOWPayments is completing the settlement.' },
sending: { percent: 85, label: 'Settlement in progress', detail: 'Funds are being sent to the plugin author. Access will unlock automatically.' },
finished: { percent: 100, label: 'Payment complete', detail: 'Your purchase is complete and the plugin is ready to download.' },
completed: { percent: 100, label: 'Payment complete', detail: 'Your purchase is complete and the plugin is ready to download.' },
failed: { percent: 100, label: 'Payment failed', detail: 'The payment could not be completed. Please start a new checkout.' },
expired: { percent: 100, label: 'Payment request expired', detail: 'This payment request expired. Please start a new checkout.' },
refunded: { percent: 100, label: 'Payment refunded', detail: 'The payment was refunded and plugin access was not granted.' }
};

function PaymentProgress({ status }) {
const normalizedStatus = String(status || 'waiting').toLowerCase();
const progress = PAYMENT_PROGRESS[normalizedStatus] || {
  percent: 30,
  label: 'Payment is being processed',
  detail: 'We are checking the payment provider for the latest update.'
};
const isError = ['failed', 'expired', 'refunded'].includes(normalizedStatus);
return (
  <div style={{...styles.paymentProgress, ...(isError ? styles.paymentProgressError : {})}} role="status" aria-live="polite">
    <div style={styles.paymentProgressHeader}>
      <strong>{progress.label}</strong>
      <span>{progress.percent}%</span>
    </div>
    <div style={styles.paymentProgressTrack}>
      <div style={{...styles.paymentProgressFill, width: `${progress.percent}%`, ...(isError ? styles.paymentProgressFillError : {})}} />
    </div>
    <span style={styles.paymentProgressDetail}>{progress.detail}</span>
    {!isError && <span style={styles.paymentProgressSteps}>Payment sent → Blockchain confirmation → Settlement → Access unlocked</span>}
  </div>
);
}

function PluginDetailPage() {
const { pluginId, slug } = useParams();
const navigate = useNavigate();
const [plugin, setPlugin] = useState(null);
const [reviews, setReviews] = useState([]);
const [bugs, setBugs] = useState([]);
const [fileTree, setFileTree] = useState([]);
const [selectedFile, setSelectedFile] = useState(null);
const [activeTab, setActiveTab] = useState('overview');
const [loading, setLoading] = useState(true);
const [resolvedPluginId, setResolvedPluginId] = useState(null);
const [slugMatches, setSlugMatches] = useState([]);
const [gitLog, setGitLog] = useState([]);
const [hasMarketplaceAccess, setHasMarketplaceAccess] = useState(true);
const [termsAccepted, setTermsAccepted] = useState(false);
const [licenseAccepted, setLicenseAccepted] = useState(false);
const [checkoutBusy, setCheckoutBusy] = useState(false);
const [checkoutMessage, setCheckoutMessage] = useState('');
const [payCurrency, setPayCurrency] = useState('btc');
const [payCurrencies, setPayCurrencies] = useState(['btc', 'eth']);
const [estimate, setEstimate] = useState(null);
const [paymentInstructions, setPaymentInstructions] = useState(null);
const [paymentStatus, setPaymentStatus] = useState('');
const [showPaymentModal, setShowPaymentModal] = useState(false);
const [expandedPaths, setExpandedPaths] = useState(() => new Set());
const [token] = useState(localStorage.getItem('token'));
const [userId] = useState(localStorage.getItem('userId'));
const effectivePaymentStatus = String(paymentStatus || paymentInstructions?.status || '').toLowerCase();
const canCancelPayment = Boolean(paymentInstructions) && (!effectivePaymentStatus || effectivePaymentStatus === 'waiting');

useEffect(() => {
loadPluginData();
}, [pluginId, slug]);

useEffect(() => {
  if (plugin) pluginSeo(plugin);
}, [plugin]);

useEffect(() => {
  if (!plugin?.isPaid || hasMarketplaceAccess || !resolvedPluginId || !token || plugin.paymentProvider !== 'nowpayments') return undefined;
  let active = true;
  const refresh = async () => {
    try {
      const response = await axios.get(`${API_BASE}/marketplace/plugins/${resolvedPluginId}/estimate?payCurrency=${encodeURIComponent(payCurrency)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (active) setEstimate(response.data);
    } catch (error) { if (active) setEstimate(null); }
  };
  refresh();
  const timer = setInterval(refresh, 30000);
  return () => { active = false; clearInterval(timer); };
}, [plugin, hasMarketplaceAccess, resolvedPluginId, token, payCurrency]);

useEffect(() => {
  if (!paymentInstructions?.providerOrderId || !token) return undefined;
  let active = true;
  let timer;
  const refreshStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/marketplace/checkout/${encodeURIComponent(paymentInstructions.providerOrderId)}/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (!active) return;
      setPaymentStatus(response.data.status || 'waiting');
      if (response.data.purchased) {
        clearInterval(timer);
        setHasMarketplaceAccess(true);
        setPaymentStatus('completed');
        setPaymentInstructions(null);
        setShowPaymentModal(false);
        setCheckoutMessage('Payment completed. Your download is ready.');
      }
    } catch (_error) {}
  };
  refreshStatus();
  timer = setInterval(refreshStatus, 5000);
  return () => { active = false; clearInterval(timer); };
}, [paymentInstructions, token]);

const loadPluginData = async () => {
setLoading(true);
setSlugMatches([]);
setSelectedFile(null);
setFileTree([]);
setReviews([]);
setBugs([]);
setGitLog([]);
setExpandedPaths(new Set());
setResolvedPluginId(null);
try {
  const requestedId = pluginId || (slug && /^\d+$/.test(slug) ? slug : null);
  let pluginData = null;

  if (requestedId) {
    const pluginRes = await axios.get(`${API_BASE}/plugin/${requestedId}`);
    pluginData = pluginRes.data;
  } else if (slug) {
    const slugRes = await axios.get(`${API_BASE}/plugin/slug/${slug}`);
    const matches = slugRes.data || [];
    if (matches.length === 1) {
      pluginData = matches[0];
    } else if (matches.length > 1) {
      setSlugMatches(matches);
    }
  }

  if (pluginData) {
    setPlugin(pluginData);
    setPayCurrency(pluginData.payCurrency || 'btc');
    setResolvedPluginId(pluginData.id);
    const authConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    try {
      const providerResponse = await axios.get(`${API_BASE}/marketplace/providers`);
      setPayCurrencies(providerResponse.data?.payCurrencies || ['btc', 'eth']);
    } catch (_providerError) {}
    const query = new URLSearchParams(window.location.search);
    const returnedOrderId = query.get('token');
    if (query.get('checkout') === 'paypal' && returnedOrderId && token) {
      try {
        await axios.post(`${API_BASE}/marketplace/checkout/${encodeURIComponent(returnedOrderId)}/capture`, {}, authConfig);
        setCheckoutMessage('Payment completed. Your download is ready.');
        window.history.replaceState({}, '', window.location.pathname);
      } catch (captureError) {
        setCheckoutMessage(captureError.response?.data?.message || 'PayPal payment could not be completed.');
      }
    } else if (query.get('checkout') === 'cancelled') {
      setCheckoutMessage('Checkout was cancelled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    let access = !pluginData.isPaid;
    try {
      const accessResponse = await axios.get(`${API_BASE}/marketplace/plugins/${pluginData.id}/access`, authConfig);
      access = Boolean(accessResponse.data?.hasAccess);
    } catch (_accessError) {
      access = !pluginData.isPaid;
    }
    setHasMarketplaceAccess(access);
    const [reviewsRes, bugsRes] = await Promise.all([
      axios.get(`${API_BASE}/review/plugin/${pluginData.id}`),
      axios.get(`${API_BASE}/bugreport/plugin/${pluginData.id}`)
    ]);
    setReviews(reviewsRes.data);
    setBugs(bugsRes.data);
    if (access) {
      try {
        const [filesRes, gitRes] = await Promise.all([
          axios.get(`${API_BASE}/plugin/${pluginData.id}/files`, authConfig),
          axios.get(`${API_BASE}/plugin/${pluginData.id}/gitlog`, authConfig)
        ]);
        setFileTree(filesRes.data || []);
        setGitLog(gitRes.data || []);
      } catch (_repositoryError) {
        setFileTree([]);
        setGitLog([]);
      }
    }
  } else {
    setPlugin(null);
  }
} catch (error) {
  console.error('Error loading plugin:', error);
  setPlugin(null);
}
setLoading(false);

};

const toggleFolder = (path) => {
setExpandedPaths((prev) => {
  const next = new Set(prev);
  if (next.has(path)) {
    next.delete(path);
  } else {
    next.add(path);
  }
  return next;
});
};

const downloadPlugin = async () => {
try {
const response = await axios.get(`${API_BASE}/plugin/${resolvedPluginId}/download`, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
const downloadUrl = response.data?.downloadUrl;
if (!downloadUrl) throw new Error('The server did not return a download link.');
const secureDownloadUrl = window.location.protocol === 'https:' && downloadUrl.startsWith('http://')
  ? downloadUrl.replace(/^http:/, 'https:')
  : downloadUrl;
window.location.assign(secureDownloadUrl);
} catch (error) {
alert(error.response?.data?.message || error.message || 'Failed to get download link');
}
};

const startCheckout = async () => {
if (!token) {
  navigate('/login');
  return;
}
setCheckoutBusy(true);
setCheckoutMessage('');
try {
  const cleanUrl = `${window.location.origin}${window.location.pathname}`;
  const response = await axios.post(`${API_BASE}/marketplace/plugins/${resolvedPluginId}/checkout`, {
    provider: plugin.paymentProvider || 'nowpayments',
    payCurrency,
    termsAccepted,
    licenseAccepted,
    returnUrl: `${cleanUrl}?checkout=paypal`,
    cancelUrl: `${cleanUrl}?checkout=cancelled`
  }, { headers: { Authorization: `Bearer ${token}` } });
  if (response.data?.alreadyPurchased) {
    setHasMarketplaceAccess(true);
    setCheckoutMessage('You already own this plugin.');
  } else if (response.data?.approvalUrl) {
    window.location.assign(response.data.approvalUrl);
  } else {
    setPaymentInstructions(response.data);
    setPaymentStatus(response.data.status || 'waiting');
  }
} catch (error) {
  setCheckoutMessage(error.response?.data?.message || 'Unable to start checkout.');
}
setCheckoutBusy(false);
};

const cancelCheckout = () => {
setPaymentInstructions(null);
setPaymentStatus('');
setShowPaymentModal(false);
setCheckoutMessage('Payment cancelled on this page. Do not send cryptocurrency to the payment address. Cryptocurrency already sent cannot be cancelled.');
};

const closePaymentModal = () => {
if (checkoutBusy) return;
if (paymentInstructions && canCancelPayment) {
  cancelCheckout();
  return;
}
setShowPaymentModal(false);
};

if (loading) {
return <div style={styles.loading}>Loading plugin details...</div>;
}

if (!plugin) {
if (slugMatches.length > 1) {
  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <button onClick={() => navigate('/')} style={styles.backBtn}>
          â† Back to Plugins
        </button>
      </header>
      <div style={styles.tabContent}>
        <h2>Multiple plugins found</h2>
        <p>Choose the plugin you want to view.</p>
        <div style={styles.matchList}>
          {slugMatches.map((match) => (
            <button
              key={match.id}
              onClick={() => navigate(`/plugin/${match.slug || slug}/${match.id}`)}
              style={styles.matchBtn}
            >
              {match.name} (v{match.version})
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
return <div style={styles.error}>Plugin not found</div>;
}

const isAuthor = userId === plugin.userId;
const isPending = plugin.status === 0 || plugin.status === 'Pending';
const lastUpdatedDate = toClientDate(plugin.updatedAt || plugin.publishedAt || plugin.createdAt);

return (
<div className="plugin-detail-page" style={styles.container}>
<header className="plugin-detail-header" style={styles.header}>
<button onClick={() => navigate('/')} style={styles.backBtn}>
← Back to Plugins
</button>
</header>

{isPending && (
<div style={styles.pendingBanner}>
Pending approval
</div>
)}

  <div className="plugin-detail-hero" style={styles.heroSection}>
    <div className="plugin-detail-hero-content" style={styles.heroContent}>
      <div style={styles.heroLeft}>
        <h1 style={styles.pluginTitle}>{plugin.name}</h1>
        <p style={styles.pluginDesc}>{plugin.readmeParsed?.summary || plugin.description}</p>
        <div style={styles.metaInfo}>
          <span style={styles.metaItem}>
            <strong>Author:</strong> {plugin.authorName}
          </span>
          <span style={styles.metaItem}>
            <strong>Version:</strong> {plugin.version}
          </span>
          <span style={styles.metaItem}>
            <strong>Last updated:</strong> {lastUpdatedDate ? lastUpdatedDate.toLocaleString() : ''}
          </span>
          <span style={styles.metaItem}>
            <strong>Downloads:</strong> {plugin.downloads.toLocaleString()}
          </span>
          <span style={styles.metaItem}>
            ⭐ {plugin.averageRating.toFixed(1)} ({reviews.length} reviews)
          </span>
        </div>
        <div style={styles.tags}>
          {plugin.tags?.split(',').map((tag, i) => (
            <span key={i} style={styles.tag}>{tag.trim()}</span>
          ))}
        </div>
      </div>
      <div className="plugin-detail-actions" style={styles.heroRight}>
        {plugin.isPaid && !hasMarketplaceAccess ? (
          <button onClick={() => setShowPaymentModal(true)} style={styles.compactBuyBtn}>
            {token ? `Buy for ${plugin.currency} ${Number(plugin.price).toFixed(2)}` : 'Buy Now'}
          </button>
        ) : (
          <button onClick={downloadPlugin} style={styles.downloadBtn}>📥 Download Plugin</button>
        )}
        {checkoutMessage && <div style={styles.checkoutMessage}>{checkoutMessage}</div>}
        <div style={styles.stats}>
          <div style={styles.statItem}>
            <span style={styles.statNumber}>{plugin.downloads}</span>
            <span style={styles.statLabel}>Downloads</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statNumber}>{reviews.length}</span>
            <span style={styles.statLabel}>Reviews</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statNumber}>{bugs.length}</span>
            <span style={styles.statLabel}>Issues</span>
          </div>
        </div>
      </div>
      {showPaymentModal && plugin.isPaid && !hasMarketplaceAccess && (
        <div style={styles.paymentModalOverlay} role="presentation">
          <div style={styles.paymentModal} role="dialog" aria-modal="true" aria-labelledby="payment-modal-title">
            <div style={styles.paymentModalHeader}>
              <h2 id="payment-modal-title" style={styles.paymentModalTitle}>Purchase {plugin.name}</h2>
              <button type="button" onClick={closePaymentModal} disabled={checkoutBusy} aria-label="Close payment" style={styles.paymentModalClose}>×</button>
            </div>
            {!paymentInstructions && <>
              {plugin.paymentProvider === 'nowpayments' && <>
                <label style={styles.acceptanceLabel}>Pay cryptocurrency</label>
                <select value={payCurrency} onChange={(event) => setPayCurrency(event.target.value)} style={styles.select}>
                  {payCurrencies.map(currency => <option key={currency} value={currency}>{currency.toUpperCase()}</option>)}
                </select>
                <div style={styles.checkoutMessage}>{estimate ? `Live estimate: ${Number(estimate.payAmount).toPrecision(8)} ${String(estimate.payCurrency).toUpperCase()} (refreshes every 30 seconds)` : token ? 'Loading live crypto estimate...' : 'Sign in to view the live crypto estimate.'}</div>
              </>}
              <div style={styles.agreementText}><strong>Download access:</strong> {plugin.downloadAccessDurationMonths === 6 ? '6 months from purchase' : plugin.downloadAccessDurationMonths === 12 ? '1 year from purchase' : 'No duration limit'}</div>
              <label style={styles.acceptanceLabel}><input type="checkbox" checked={termsAccepted} onChange={(event) => setTermsAccepted(event.target.checked)} /> I accept the terms of sale.</label>
              <div style={styles.agreementText}>{plugin.termsOfSale}</div>
              <label style={styles.acceptanceLabel}><input type="checkbox" checked={licenseAccepted} onChange={(event) => setLicenseAccepted(event.target.checked)} /> I accept the plugin license agreement.</label>
              <div style={styles.agreementText}>{plugin.licenseAgreement || 'No additional license text was supplied by the author.'}</div>
              <button onClick={startCheckout} disabled={checkoutBusy || !termsAccepted || !licenseAccepted} style={{...styles.compactBuyBtn, ...styles.modalBuyBtn, opacity: checkoutBusy || !termsAccepted || !licenseAccepted ? 0.55 : 1}}>
                {checkoutBusy ? 'Starting checkout...' : token ? `Buy Now for ${plugin.currency} ${Number(plugin.price).toFixed(2)}` : 'Buy Now'}
              </button>
            </>}
            {paymentInstructions?.payAddress && <div style={styles.paymentInstructions}>
              <PaymentProgress status={paymentStatus || paymentInstructions.status} />
              <strong>Send exactly {paymentInstructions.payAmount} {String(paymentInstructions.payCurrency).toUpperCase()}</strong>
              <div style={styles.paymentQrPanel}>
                <QRCodeSVG
                  value={buildCryptoPaymentUri(paymentInstructions.payCurrency, paymentInstructions.payAddress, paymentInstructions.payAmount)}
                  size={210}
                  level="M"
                  marginSize={2}
                  title={`Pay ${paymentInstructions.payAmount} ${String(paymentInstructions.payCurrency).toUpperCase()}`}
                />
                <span style={styles.paymentQrCaption}>Scan with a compatible cryptocurrency wallet.</span>
              </div>
              <span>to this address:</span>
              <code style={styles.paymentAddress}>{paymentInstructions.payAddress}</code>
              {paymentInstructions.expiresAt && <span>Payment request expires {new Date(paymentInstructions.expiresAt).toLocaleString()}.</span>}
              <span>Status updates automatically every 5 seconds. You can keep this window open while the blockchain confirms the payment.</span>
              {canCancelPayment && <button type="button" onClick={cancelCheckout} style={styles.cancelPaymentBtn}>Cancel Payment</button>}
            </div>}
          </div>
        </div>
      )}
    </div>
  </div>

  {plugin.media && plugin.media.length > 0 && (
    <div className="plugin-detail-gallery" style={styles.gallery}>
      {plugin.media.map((media, i) => (
        <div key={i} style={styles.galleryItem}>
          {media.type === 0 ? (
            <img src={media.s3Url} alt={`Screenshot ${i + 1}`} style={styles.galleryImage} />
          ) : (
            <video controls style={styles.galleryVideo}>
              <source src={media.s3Url} type="video/mp4" />
            </video>
          )}
        </div>
      ))}
    </div>
  )}

  <div className="plugin-detail-tabs" style={styles.tabs}>
    {['overview', ...(hasMarketplaceAccess ? ['files'] : []), 'reviews', 'bugs', 'versions', ...(hasMarketplaceAccess ? ['git log'] : [])].map(tab => (
      <button
        key={tab}
        onClick={() => setActiveTab(tab)}
        style={{
          ...styles.tab,
          ...(activeTab === tab ? styles.activeTab : {})
        }}
      >
        {tab.charAt(0).toUpperCase() + tab.slice(1)}
      </button>
    ))}
  </div>

  <div className="plugin-detail-content" style={styles.content}>
    {activeTab === 'overview' && (
      <OverviewTab plugin={plugin} />
    )}
      {activeTab === 'files' && (
        <FilesTab 
        fileTree={fileTree} 
        selectedFile={selectedFile}
        expandedPaths={expandedPaths}
        onToggleFolder={toggleFolder}
        onSelectFile={async (file) => {
          if (!file?.path) {
            return;
          }
          setSelectedFile({ ...file, content: 'Loading...' });
          try {
            const response = await axios.get(`${API_BASE}/plugin/${resolvedPluginId}/file`, {
              params: { path: file.path },
              headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });
            setSelectedFile({
              ...file,
              content: response.data?.content || '',
              binary: response.data?.binary
            });
          } catch (err) {
            setSelectedFile({ ...file, content: 'Failed to load file.' });
          }
        }}
        />
      )}
    {activeTab === 'reviews' && (
      <ReviewsTab 
        reviews={reviews}
        pluginId={plugin.id}
        isAuthor={isAuthor}
        onReviewAdded={loadPluginData}
        token={token}
      />
    )}
    {activeTab === 'bugs' && (
      <BugsTab
        bugs={bugs}
        pluginId={plugin.id}
        isAuthor={isAuthor}
        onBugAdded={loadPluginData}
        token={token}
      />
    )}
    {activeTab === 'versions' && (
      <VersionsTab plugin={plugin} token={token} />
    )}
    {activeTab === 'git log' && (
      <GitLogTab entries={gitLog} />
    )}
  </div>
</div>

);
}

function OverviewTab({ plugin }) {
const parsed = plugin.readmeParsed;
const [galleryIndex, setGalleryIndex] = useState(0);
const [lightbox, setLightbox] = useState({ open: false, url: '', caption: '' });
const screenshots = parsed?.screenshots || [];
const hasScreenshots = screenshots.length > 0;
const currentShot = hasScreenshots ? screenshots[galleryIndex] : null;
const galleryUrl = currentShot
  ? `${API_BASE}/plugin/${plugin.id}/assets?path=${encodeURIComponent(`resources/${currentShot.file}`)}`
  : null;

if (!parsed || (!parsed.description && !parsed.installation && !parsed.requirements && !parsed.serverRequirements && !parsed.clientRequirements && !parsed.specialNoteOnRequirements && !parsed.faq?.length && !parsed.changelog?.length && !parsed.additionalSections?.length)) {
  return (
    <div style={styles.tabContent}>
      <h2>About This Plugin</h2>
      <div style={styles.readmeContent}>
        <pre style={styles.readmePre}>{plugin.readmeContent}</pre>
      </div>
    </div>
  );
}

return (
  <div style={styles.tabContent}>
    <h2>About This Plugin</h2>
    <div style={styles.readmeContent}>
      {(parsed.contributors || parsed.types?.length || parsed.tags?.length || parsed.requiresAtLeast || parsed.testedUpTo || parsed.stableTag || parsed.license) && (
        <div style={styles.readmeMeta}>
          {parsed.contributors && <div><strong>Contributors:</strong> {parsed.contributors}</div>}
          {parsed.types?.length > 0 && <div><strong>Type:</strong> {parsed.types.join(', ')}</div>}
          {parsed.tags?.length > 0 && <div><strong>Tags:</strong> {parsed.tags.join(', ')}</div>}
          {parsed.requiresAtLeast && <div><strong>Requires at least:</strong> {parsed.requiresAtLeast}</div>}
          {parsed.testedUpTo && <div><strong>Tested up to:</strong> {parsed.testedUpTo}</div>}
          {parsed.stableTag && <div><strong>Stable tag:</strong> {parsed.stableTag}</div>}
          {parsed.license && <div><strong>License:</strong> {parsed.license}</div>}
        </div>
      )}

      {parsed.summary && (
        <section style={styles.readmeSection}>
          <h3>Summary</h3>
          <p>{parsed.summary}</p>
        </section>
      )}

      {parsed.description && (
        <section style={styles.readmeSection}>
          <h3>Description</h3>
          <p style={styles.readmeParagraph}>{parsed.description}</p>
        </section>
      )}

      {parsed.installation && (
        <section style={styles.readmeSection}>
          <h3>Installation</h3>
          <pre style={styles.readmePre}>{parsed.installation}</pre>
        </section>
      )}

      {parsed.additionalSections?.filter(section => !/^seo\s+keywords?$/i.test(String(section.title || '').trim())).map((section, idx) => (
        <section key={`${section.title}-${idx}`} style={styles.readmeSection}>
          <h3>{section.title}</h3>
          <pre style={styles.readmePre}>{section.content}</pre>
        </section>
      ))}

      {hasScreenshots && (
        <section style={styles.readmeSection}>
          <h3>Screenshots</h3>
          <div style={styles.gallery}>
            <button
              style={styles.galleryBtn}
              onClick={() => setGalleryIndex((galleryIndex - 1 + screenshots.length) % screenshots.length)}
              type="button"
            >
              Prev
            </button>
            <div style={styles.galleryFrame}>
              <img
                src={galleryUrl}
                alt={currentShot.file}
                style={styles.galleryImage}
                onClick={() => setLightbox({ open: true, url: galleryUrl, caption: currentShot.caption })}
              />
              {currentShot.caption && <div style={styles.galleryCaption}>{currentShot.caption}</div>}
            </div>
            <button
              style={styles.galleryBtn}
              onClick={() => setGalleryIndex((galleryIndex + 1) % screenshots.length)}
              type="button"
            >
              Next
            </button>
          </div>
          <div style={styles.galleryHint}>
            {galleryIndex + 1} / {screenshots.length}
          </div>
        </section>
      )}

      {parsed.serverRequirements && (
        <section style={styles.readmeSection}>
          <h3>Server Requirements</h3>
          <pre style={styles.readmePre}>{parsed.serverRequirements}</pre>
        </section>
      )}

      {parsed.clientRequirements && (
        <section style={styles.readmeSection}>
          <h3>Client Requirements</h3>
          <pre style={styles.readmePre}>{parsed.clientRequirements}</pre>
        </section>
      )}

      {parsed.requirements && (
        <section style={styles.readmeSection}>
          <h3>Requirements</h3>
          <pre style={styles.readmePre}>{parsed.requirements}</pre>
        </section>
      )}

      {parsed.specialNoteOnRequirements && (
        <section style={styles.readmeSection}>
          <h3>Special Note on Requirements</h3>
          <pre style={styles.readmePre}>{parsed.specialNoteOnRequirements}</pre>
        </section>
      )}

      {parsed.faq?.length > 0 && (
        <section style={styles.readmeSection}>
          <h3>Frequently Asked Questions</h3>
          <div style={styles.readmeFaq}>
            {parsed.faq.map((item, idx) => (
              <div key={idx} style={styles.readmeFaqItem}>
                <strong>{item.question}</strong>
                <p>{item.answer}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {parsed.changelog?.length > 0 && (
        <section style={styles.readmeSection}>
          <h3>Changelog</h3>
          {parsed.changelog.map((entry, idx) => (
            <div key={idx} style={styles.readmeChangelog}>
              <strong>{entry.version}</strong>
              {entry.notes && <pre style={styles.readmePre}>{entry.notes}</pre>}
            </div>
          ))}
        </section>
      )}
    </div>
    {lightbox.open && (
      <div style={styles.lightboxOverlay} onClick={() => setLightbox({ open: false, url: '', caption: '' })}>
        <div style={styles.lightboxContent} onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            style={styles.lightboxClose}
            onClick={() => setLightbox({ open: false, url: '', caption: '' })}
          >
            Close
          </button>
          <img src={lightbox.url} alt="" style={styles.lightboxImage} />
          {lightbox.caption && <div style={styles.lightboxCaption}>{lightbox.caption}</div>}
        </div>
      </div>
    )}
  </div>
);
}

function FilesTab({ fileTree, selectedFile, onSelectFile, expandedPaths, onToggleFolder }) {
const renderFileTree = (items, level = 0, parentPath = '') => {
return items.map((item, i) => {
  const isFolder = item.type === 'folder';
  const nodePath = item.path || (parentPath ? `${parentPath}/${item.name}` : item.name);
  const isExpanded = expandedPaths.has(nodePath);
  return (
    <div key={`${nodePath}-${i}`} style={{marginLeft: level * 20}}>
      <div
        onClick={() => (isFolder ? onToggleFolder(nodePath) : onSelectFile(item))}
        style={{
          ...styles.fileItem,
          cursor: 'pointer',
          fontWeight: selectedFile?.name === item.name ? 'bold' : 'normal'
        }}
      >
        <span style={styles.fileToggle}>{isFolder ? (isExpanded ? '[-]' : '[+]') : '   '}</span>
        <span>{item.name}</span>
      </div>
      {isFolder && isExpanded && item.children && renderFileTree(item.children, level + 1, nodePath)}
    </div>
  );
});
};

return (
<div style={styles.tabContent}>
<div style={styles.filesLayout}>
<div style={styles.fileTreePanel}>
<h3>File Browser</h3>
<div style={styles.fileTreeList}>{renderFileTree(fileTree)}</div>
</div>
<div style={styles.fileContentPanel}>
{selectedFile ? (
<>
<h3>{selectedFile.name}</h3>
{selectedFile.binary ? (
  <div style={styles.emptyState}>Binary file preview not available.</div>
) : (
  <pre style={styles.codeBlock}>{selectedFile.content}</pre>
)}
</>
) : (
<div style={styles.emptyState}>
Select a file to view its contents
</div>
)}
</div>
</div>
</div>
);
}

function ReviewsTab({ reviews, pluginId, isAuthor, onReviewAdded, token }) {
const [showForm, setShowForm] = useState(false);
const [rating, setRating] = useState(5);
const [comment, setComment] = useState('');
const [replyTo, setReplyTo] = useState(null);
const [replyText, setReplyText] = useState('');

const submitReview = async () => {
if (!token) {
alert('Please login to submit a review');
return;
}

try {
  await axios.post(`${API_BASE}/review`, {
    pluginId,
    rating,
    comment
  }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
  alert('Review submitted!');
  setShowForm(false);
  setComment('');
  onReviewAdded();
} catch (error) {
  alert('Failed to submit review');
}

};

const submitReply = async (reviewId) => {
try {
await axios.post(`${API_BASE}/review/${reviewId}/reply`, {
reply: replyText
}, {
headers: { Authorization: `Bearer ${token}` }
});

  alert('Reply submitted!');
  setReplyTo(null);
  setReplyText('');
  onReviewAdded();
} catch (error) {
  alert('Failed to submit reply');
}

};

return (
<div style={styles.tabContent}>
<div style={styles.reviewHeader}>
<h2>Reviews ({reviews.length})</h2>
{!isAuthor && token && (
<button onClick={() => setShowForm(!showForm)} style={styles.primaryBtn}>
Write a Review
</button>
)}
</div>

  {showForm && (
    <div style={styles.reviewForm}>
      <h3>Write Your Review</h3>
      <div style={styles.ratingSelector}>
        <label>Rating: </label>
        {[1, 2, 3, 4, 5].map(r => (
          <span
            key={r}
            onClick={() => setRating(r)}
            style={{
              cursor: 'pointer',
              fontSize: '24px',
              color: r <= rating ? '#f39c12' : '#ddd'
            }}
          >
            ★
          </span>
        ))}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Share your experience with this plugin..."
        style={styles.textarea}
        rows={5}
      />
      <div style={styles.formActions}>
        <button onClick={submitReview} style={styles.primaryBtn}>Submit Review</button>
        <button onClick={() => setShowForm(false)} style={styles.secondaryBtn}>Cancel</button>
      </div>
    </div>
  )}

  <div style={styles.reviewsList}>
    {reviews.map(review => (
      <div key={review.id} style={styles.reviewItem}>
        <div style={styles.reviewHeader}>
          <div>
            <strong>{review.userName}</strong>
            <div style={styles.stars}>
              {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
            </div>
          </div>
          <span style={styles.reviewDate}>
            {new Date(review.createdAt).toLocaleDateString()}
          </span>
        </div>
        <p style={styles.reviewComment}>{review.comment}</p>
        
        {review.replies?.map(reply => (
          <div key={reply.id} style={styles.replyBox}>
            <strong>{reply.author} (Author):</strong> {reply.reply}
            <span style={styles.replyDate}>
              {new Date(reply.createdAt).toLocaleDateString()}
            </span>
          </div>
        ))}

        {isAuthor && replyTo !== review.id && (
          <button
            onClick={() => setReplyTo(review.id)}
            style={styles.replyBtn}
          >
            Reply
          </button>
        )}

        {replyTo === review.id && (
          <div style={styles.replyForm}>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write your reply..."
              style={styles.textarea}
              rows={3}
            />
            <div style={styles.formActions}>
              <button onClick={() => submitReply(review.id)} style={styles.primaryBtn}>
                Submit Reply
              </button>
              <button onClick={() => setReplyTo(null)} style={styles.secondaryBtn}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
</div>

);
}

function BugsTab({ bugs, pluginId, isAuthor, onBugAdded, token }) {
const [showForm, setShowForm] = useState(false);
const [title, setTitle] = useState('');
const [description, setDescription] = useState('');
const [severity, setSeverity] = useState(1);
const [selectedBug, setSelectedBug] = useState(null);
const [comment, setComment] = useState('');

const submitBug = async () => {
if (!token) {
alert('Please login to report a bug');
return;
}

try {
  await axios.post(`${API_BASE}/bugreport`, {
    pluginId,
    title,
    description,
    severity
  }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
  alert('Bug report submitted!');
  setShowForm(false);
  setTitle('');
  setDescription('');
  onBugAdded();
} catch (error) {
  alert('Failed to submit bug report');
}

};

const addComment = async (bugId) => {
try {
await axios.post(`${API_BASE}/bugreport/${bugId}/comment`, {
comment
}, {
headers: { Authorization: `Bearer ${token}` }
});

  alert('Comment added!');
  setComment('');
  loadBugDetails(bugId);
} catch (error) {
  alert('Failed to add comment');
}

};

const updateStatus = async (bugId, status) => {
try {
await axios.put(`${API_BASE}/bugreport/${bugId}/status`, {
status
}, {
headers: { Authorization: `Bearer ${token}` }
});

  alert('Status updated!');
  onBugAdded();
} catch (error) {
  alert('Failed to update status');
}

};

const loadBugDetails = async (bugId) => {
try {
const response = await axios.get(`${API_BASE}/bugreport/${bugId}`);
setSelectedBug(response.data);
} catch (error) {
console.error('Error loading bug details');
}
};

const getSeverityColor = (sev) => {
const colors = ['#27ae60', '#f39c12', '#e67e22', '#e74c3c'];
return colors[sev] || '#95a5a6';
};

const getStatusColor = (status) => {
const colors = {
Open: '#3498db',
InProgress: '#f39c12',
Resolved: '#27ae60',
Closed: '#95a5a6',
WontFix: '#e74c3c'
};
return colors[status] || '#95a5a6';
};

return (
<div style={styles.tabContent}>
<div style={styles.reviewHeader}>
<h2>Issues & Bugs ({bugs.length})</h2>
{token && (
<button onClick={() => setShowForm(!showForm)} style={styles.primaryBtn}>
Report an Issue
</button>
)}
</div>

  {showForm && (
    <div style={styles.bugForm}>
      <h3>Report an Issue</h3>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Issue title"
        style={styles.input}
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Describe the issue in detail..."
        style={styles.textarea}
        rows={5}
      />
      <div style={styles.formGroup}>
        <label>Severity: </label>
        <select value={severity} onChange={(e) => setSeverity(parseInt(e.target.value))} style={styles.select}>
          <option value={0}>Low</option>
          <option value={1}>Medium</option>
          <option value={2}>High</option>
          <option value={3}>Critical</option>
        </select>
      </div>
      <div style={styles.formActions}>
        <button onClick={submitBug} style={styles.primaryBtn}>Submit Issue</button>
        <button onClick={() => setShowForm(false)} style={styles.secondaryBtn}>Cancel</button>
      </div>
    </div>
  )}

  <div style={styles.bugsList}>
    {bugs.map(bug => (
      <div key={bug.id} style={styles.bugItem} onClick={() => loadBugDetails(bug.id)}>
        <div style={styles.bugHeader}>
          <h4 style={styles.bugTitle}>{bug.title}</h4>
          <div style={styles.bugBadges}>
            <span style={{...styles.badge, background: getSeverityColor(bug.severity)}}>
              {bug.severity}
            </span>
            <span style={{...styles.badge, background: getStatusColor(bug.status)}}>
              {bug.status}
            </span>
          </div>
        </div>
        <p style={styles.bugDesc}>{bug.description}</p>
        <div style={styles.bugMeta}>
          <span>Reported by {bug.author.userName}</span>
          <span>{new Date(bug.createdAt).toLocaleDateString()}</span>
          <span>{bug.commentCount} comments</span>
        </div>

        {selectedBug?.id === bug.id && (
          <div style={styles.bugDetails}>
            {selectedBug.comments?.map(c => (
              <div key={c.id} style={{
                ...styles.bugComment,
                ...(c.isAuthorResponse ? styles.authorComment : {})
              }}>
                <strong>{c.user.userName}</strong>
                {c.isAuthorResponse && <span style={styles.authorBadge}>Author</span>}
                <p>{c.comment}</p>
                <span style={styles.commentDate}>
                  {new Date(c.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}

            {token && (
              <div style={styles.commentForm}>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment..."
                  style={styles.textarea}
                  rows={3}
                />
                <button onClick={() => addComment(bug.id)} style={styles.primaryBtn}>
                  Add Comment
                </button>
              </div>
            )}

            {isAuthor && (
              <div style={styles.statusActions}>
                <button onClick={() => updateStatus(bug.id, 1)} style={styles.statusBtn}>
                  Mark In Progress
                </button>
                <button onClick={() => updateStatus(bug.id, 2)} style={styles.statusBtn}>
                  Mark Resolved
                </button>
                <button onClick={() => updateStatus(bug.id, 3)} style={styles.statusBtn}>
                  Close
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    ))}
  </div>
</div>

);
}

function VersionsTab({ plugin, token }) {
const versions = (plugin.versions || [])
  .slice()
  .sort((a, b) => new Date(b.releasedAt) - new Date(a.releasedAt));
const [openReadmeId, setOpenReadmeId] = useState(null);

const downloadVersion = async (versionId) => {
try {
  const response = await axios.get(`${API_BASE}/plugin/version/${versionId}/download`, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
  window.open(response.data.downloadUrl, '_blank');
} catch (error) {
  alert(error.response?.data?.message || 'Failed to get download link');
}
};

return (
<div style={styles.tabContent}>
<h2>Version History</h2>
<div style={styles.versionsList}>
{versions.length === 0 && (
  <div style={styles.emptyState}>No version history yet.</div>
)}
{versions.map(v => (
<div key={v.id} style={styles.versionItem}>
<div style={styles.versionHeader}>
<h3>Version {v.version}</h3>
<div style={styles.versionMeta}>
  <span>{formatDateOnly(v.releasedAt)}</span>
  <button
    type="button"
    style={styles.secondaryBtn}
    onClick={() => setOpenReadmeId(openReadmeId === v.id ? null : v.id)}
  >
    {openReadmeId === v.id ? 'Hide README' : 'View README'}
  </button>
</div>
</div>
<p>{v.releaseNotes}</p>
<div style={styles.versionActions}>
  <button style={styles.downloadVersionBtn} onClick={() => downloadVersion(v.id)}>
    Download v{v.version}
  </button>
</div>
{openReadmeId === v.id && (
  <pre style={styles.readmePre}>
    {v.readmeContent || 'README not stored for this version.'}
  </pre>
)}
</div>
))}
</div>
</div>
);
}

function GitLogTab({ entries }) {
return (
<div style={styles.tabContent}>
<h2>Git Log</h2>
{!entries || entries.length === 0 ? (
  <div style={styles.emptyState}>No git history available.</div>
) : (
  <div style={styles.gitLogList}>
    {entries.map((entry) => (
      <div key={entry.hash} style={styles.gitLogItem}>
        {(() => {
          const name = entry.author || '';
          const isUnknown = !name || name.toLowerCase() === 'unknown';
          const authorLabel = isUnknown ? (entry.authorEmail || 'Unknown author') : name;
          const subject = entry.subject && entry.subject.toLowerCase() !== 'unknown'
            ? entry.subject
            : 'No commit message';
          return (
        <div style={styles.gitLogHeader}>
          <div>
            <strong>{subject}</strong>
            {entry.body && <div style={styles.gitLogBody}>{entry.body}</div>}
          </div>
          <div style={styles.gitLogMeta}>
            <div>{authorLabel}</div>
            <div>{entry.date ? toClientDate(entry.date)?.toLocaleString() : ''}</div>
            <div style={styles.gitLogHash}>{entry.hash?.slice(0, 8)}</div>
          </div>
        </div>
          );
        })()}
        {entry.files?.length > 0 && (
          <div style={styles.gitLogFiles}>
            {entry.files.map((file, idx) => (
              <div key={`${entry.hash}-${idx}`} style={styles.gitLogFile}>
                <span style={styles.gitLogStatus}>{file.status}</span>
                <span>{file.path}</span>
                {file.path2 && <span> → {file.path2}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
)}
</div>
);
}

function toClientDate(value) {
if (!value) {
  return null;
}
if (value instanceof Date) {
  return value;
}
if (typeof value === 'string') {
  const trimmed = value.trim();
  const hasTimezone = /[zZ]|[+-]\d{2}:\d{2}$/.test(trimmed);
  return new Date(hasTimezone ? trimmed : `${trimmed}Z`);
}
return new Date(value);
}

function formatDateOnly(value) {
const date = toClientDate(value);
return date ? date.toLocaleDateString() : '';
}

const styles = {
container: {
minHeight: '100vh',
background: '#f5f7fa',
fontFamily: 'Arial, sans-serif'
},
header: {
background: 'white',
padding: '20px 40px',
boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
},
backBtn: {
padding: '10px 20px',
background: '#667eea',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontSize: '14px'
},
heroSection: {
background: 'white',
padding: '40px',
borderBottom: '1px solid #e1e8ed'
},
heroContent: {
maxWidth: '1400px',
margin: '0 auto',
display: 'flex',
gap: '40px'
},
heroLeft: {
flex: 1
},
heroRight: {
width: '300px'
},
compactBuyBtn: {
width: '100%',
padding: '16px',
background: '#27ae60',
color: 'white',
border: 'none',
borderRadius: '8px',
fontSize: '18px',
fontWeight: 'bold',
cursor: 'pointer',
marginBottom: '20px'
},
paymentModalOverlay: {
position: 'fixed',
inset: 0,
zIndex: 2000,
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
padding: '20px',
background: 'rgba(15, 23, 42, 0.58)'
},
paymentModal: {
width: '100%',
maxWidth: '500px',
maxHeight: '90vh',
overflowY: 'auto',
boxSizing: 'border-box',
padding: '22px',
background: '#ffffff',
borderRadius: '12px',
boxShadow: '0 24px 64px rgba(15, 23, 42, 0.3)'
},
paymentModalHeader: {
display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
gap: '16px',
marginBottom: '14px'
},
paymentModalTitle: {
margin: 0,
fontSize: '22px',
color: '#1f2937'
},
paymentModalClose: {
width: '36px',
height: '36px',
padding: 0,
background: '#f3f4f6',
color: '#374151',
border: 'none',
borderRadius: '50%',
fontSize: '25px',
lineHeight: 1,
cursor: 'pointer'
},
modalBuyBtn: {
marginTop: '14px',
marginBottom: 0
},
pluginTitle: {
fontSize: '36px',
fontWeight: 'bold',
margin: '0 0 15px 0',
color: '#333'
},
pluginDesc: {
fontSize: '18px',
color: '#666',
lineHeight: '1.6',
marginBottom: '20px',
whiteSpace: 'pre-wrap'
},
metaInfo: {
display: 'flex',
flexWrap: 'wrap',
gap: '20px',
marginBottom: '20px',
fontSize: '15px',
color: '#666'
},
metaItem: {},
tags: {
display: 'flex',
flexWrap: 'wrap',
gap: '8px'
},
tag: {
padding: '6px 14px',
background: '#667eea',
color: 'white',
borderRadius: '15px',
fontSize: '13px'
},
downloadBtn: {
width: '100%',
padding: '16px',
background: '#27ae60',
color: 'white',
border: 'none',
borderRadius: '8px',
fontSize: '18px',
fontWeight: 'bold',
cursor: 'pointer',
marginBottom: '20px'
},
stats: {
display: 'grid',
gridTemplateColumns: 'repeat(3, 1fr)',
gap: '15px'
},
statItem: {
textAlign: 'center',
padding: '15px',
background: '#f8f9fa',
borderRadius: '8px'
},
statNumber: {
display: 'block',
fontSize: '24px',
fontWeight: 'bold',
color: '#667eea',
marginBottom: '5px'
},
statLabel: {
fontSize: '12px',
color: '#666'
},
gallery: {
maxWidth: '1400px',
margin: '0 auto',
padding: '30px 40px',
display: 'grid',
gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
gap: '20px'
},
galleryItem: {
borderRadius: '8px',
overflow: 'hidden',
background: 'white',
boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
},
galleryImage: {
width: '100%',
height: '250px',
objectFit: 'cover'
},
galleryVideo: {
width: '100%',
height: '250px'
},
tabs: {
maxWidth: '1400px',
margin: '0 auto',
padding: '0 40px',
display: 'flex',
gap: '10px',
borderBottom: '2px solid #e1e8ed'
},
tab: {
padding: '15px 25px',
background: 'transparent',
border: 'none',
borderBottom: '3px solid transparent',
cursor: 'pointer',
fontSize: '16px',
fontWeight: '500',
color: '#666',
transition: 'all 0.3s'
},
activeTab: {
borderBottomColor: '#667eea',
color: '#667eea',
fontWeight: 'bold'
},
content: {
maxWidth: '1400px',
margin: '0 auto',
padding: '30px 40px'
},
tabContent: {
background: 'white',
padding: '30px',
borderRadius: '8px',
boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
},
sectionHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
gap: '12px'
},
readmeContent: {
marginTop: '20px'
},
readmeMeta: {
background: '#f8fafc',
border: '1px solid #e2e8f0',
borderRadius: '8px',
padding: '12px 16px',
marginBottom: '20px',
display: 'grid',
gap: '6px',
fontSize: '14px'
},
readmePre: {
background: '#f8f9fa',
padding: '20px',
borderRadius: '8px',
overflow: 'auto',
fontSize: '14px',
lineHeight: '1.6',
whiteSpace: 'pre-wrap'
},
readmeSection: {
marginBottom: '24px'
},
readmeParagraph: {
whiteSpace: 'pre-wrap'
},
readmeFaq: {
display: 'grid',
gap: '12px'
},
readmeFaqItem: {
background: '#f8fafc',
borderRadius: '8px',
padding: '12px 16px'
},
readmeList: {
paddingLeft: '20px'
},
readmeChangelog: {
marginBottom: '16px'
},
gallery: {
display: 'grid',
gridTemplateColumns: '80px 1fr 80px',
gap: '12px',
alignItems: 'center'
},
galleryBtn: {
padding: '8px 12px',
border: '1px solid #e2e8f0',
background: '#ffffff',
borderRadius: '8px',
cursor: 'pointer'
},
galleryFrame: {
borderRadius: '12px',
overflow: 'hidden',
border: '1px solid #e2e8f0',
background: '#f8fafc',
textAlign: 'center'
},
galleryImage: {
width: '100%',
display: 'block'
},
galleryCaption: {
padding: '8px 12px',
fontSize: '13px',
color: '#475569'
},
galleryHint: {
marginTop: '8px',
fontSize: '13px',
color: '#64748b'
},
lightboxOverlay: {
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
background: 'rgba(15, 23, 42, 0.75)',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
padding: '30px',
zIndex: 2000
},
lightboxContent: {
background: '#0f172a',
padding: '16px',
borderRadius: '12px',
maxWidth: '90vw',
maxHeight: '90vh',
display: 'flex',
flexDirection: 'column',
gap: '10px',
alignItems: 'center'
},
lightboxImage: {
maxWidth: '86vw',
maxHeight: '78vh',
objectFit: 'contain',
borderRadius: '8px'
},
lightboxCaption: {
color: '#e2e8f0',
fontSize: '14px',
textAlign: 'center'
},
lightboxClose: {
alignSelf: 'flex-end',
padding: '6px 12px',
background: '#1f2937',
color: '#f8fafc',
border: '1px solid #334155',
borderRadius: '6px',
cursor: 'pointer'
},
filesLayout: {
display: 'grid',
gridTemplateColumns: '300px 1fr',
gap: '20px',
minHeight: '500px'
},
fileTreePanel: {
borderRight: '1px solid #e1e8ed',
paddingRight: '20px',
overflowX: 'auto'
},
fileTreeList: {
minWidth: 'max-content'
},
fileItem: {
padding: '8px',
marginBottom: '4px',
borderRadius: '4px',
transition: 'background 0.2s',
whiteSpace: 'nowrap',
display: 'flex',
gap: '6px',
alignItems: 'center'
},
fileToggle: {
fontFamily: 'Consolas, monospace',
color: '#64748b'
},
fileContentPanel: {
paddingLeft: '20px',
overflowX: 'auto'
},
codeBlock: {
background: '#2c3e50',
color: '#ecf0f1',
padding: '20px',
borderRadius: '8px',
overflow: 'auto',
fontSize: '14px',
lineHeight: '1.6',
whiteSpace: 'pre'
},
emptyState: {
textAlign: 'center',
padding: '100px 20px',
color: '#999'
},
reviewHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
marginBottom: '30px'
},
primaryBtn: {
padding: '12px 24px',
background: '#667eea',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontWeight: '600',
fontSize: '14px'
},
secondaryBtn: {
padding: '12px 24px',
background: '#95a5a6',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
fontSize: '14px'
},
reviewForm: {
background: '#f8f9fa',
padding: '20px',
borderRadius: '8px',
marginBottom: '30px'
},
ratingSelector: {
display: 'flex',
alignItems: 'center',
gap: '10px',
marginBottom: '15px'
},
textarea: {
width: '100%',
padding: '12px',
border: '1px solid #e1e8ed',
borderRadius: '6px',
fontSize: '14px',
fontFamily: 'inherit',
resize: 'vertical'
},
formActions: {
display: 'flex',
gap: '10px',
marginTop: '15px'
},
reviewsList: {
display: 'flex',
flexDirection: 'column',
gap: '20px'
},
reviewItem: {
padding: '20px',
border: '1px solid #e1e8ed',
borderRadius: '8px'
},
stars: {
color: '#f39c12',
fontSize: '16px',
marginTop: '5px'
},
reviewDate: {
fontSize: '13px',
color: '#999'
},
reviewComment: {
marginTop: '10px',
lineHeight: '1.6',
color: '#555'
},
replyBox: {
background: '#f0f3f7',
padding: '15px',
marginTop: '15px',
borderLeft: '3px solid #667eea',
borderRadius: '4px'
},
replyDate: {
fontSize: '12px',
color: '#999',
marginLeft: '10px'
},
replyBtn: {
padding: '8px 16px',
background: '#3498db',
color: 'white',
border: 'none',
borderRadius: '4px',
cursor: 'pointer',
fontSize: '13px',
marginTop: '10px'
},
replyForm: {
marginTop: '15px',
padding: '15px',
background: '#f8f9fa',
borderRadius: '6px'
},
bugForm: {
background: '#f8f9fa',
padding: '20px',
borderRadius: '8px',
marginBottom: '30px'
},
input: {
width: '100%',
padding: '12px',
border: '1px solid #e1e8ed',
borderRadius: '6px',
fontSize: '14px',
marginBottom: '15px'
},
select: {
padding: '10px',
border: '1px solid #e1e8ed',
borderRadius: '6px',
fontSize: '14px',
background: 'white'
},
formGroup: {
marginBottom: '15px'
},
bugsList: {
display: 'flex',
flexDirection: 'column',
gap: '20px'
},
bugItem: {
padding: '20px',
border: '1px solid #e1e8ed',
borderRadius: '8px',
cursor: 'pointer',
transition: 'box-shadow 0.2s',
background: 'white'
},
bugHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'flex-start',
marginBottom: '10px'
},
bugTitle: {
fontSize: '18px',
fontWeight: 'bold',
color: '#333',
margin: 0
},
bugBadges: {
display: 'flex',
gap: '8px',
flexShrink: 0
},
badge: {
padding: '4px 12px',
borderRadius: '12px',
color: 'white',
fontSize: '12px',
fontWeight: '600'
},
bugDesc: {
color: '#666',
lineHeight: '1.5',
marginBottom: '15px'
},
bugMeta: {
display: 'flex',
gap: '20px',
fontSize: '13px',
color: '#999'
},
bugDetails: {
marginTop: '20px',
paddingTop: '20px',
borderTop: '2px solid #e1e8ed'
},
bugComment: {
padding: '15px',
background: '#f8f9fa',
borderRadius: '6px',
marginBottom: '10px',
borderLeft: '3px solid #95a5a6'
},
authorComment: {
background: '#e8f4fd',
borderLeftColor: '#667eea'
},
authorBadge: {
marginLeft: '10px',
padding: '2px 8px',
background: '#667eea',
color: 'white',
borderRadius: '10px',
fontSize: '11px',
fontWeight: 'bold'
},
commentDate: {
display: 'block',
marginTop: '8px',
fontSize: '12px',
color: '#999'
},
commentForm: {
marginTop: '20px',
padding: '15px',
background: '#f8f9fa',
borderRadius: '6px'
},
statusActions: {
display: 'flex',
gap: '10px',
marginTop: '15px',
paddingTop: '15px',
borderTop: '1px solid #e1e8ed'
},
statusBtn: {
padding: '8px 16px',
background: '#3498db',
color: 'white',
border: 'none',
borderRadius: '4px',
cursor: 'pointer',
fontSize: '13px'
},
versionsList: {
display: 'flex',
flexDirection: 'column',
gap: '20px'
},
versionItem: {
padding: '20px',
border: '1px solid #e1e8ed',
borderRadius: '8px',
background: 'white'
},
versionHeader: {
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
marginBottom: '15px'
},
versionMeta: {
display: 'flex',
flexDirection: 'column',
alignItems: 'flex-end',
gap: '8px'
},
versionActions: {
display: 'flex',
gap: '10px',
alignItems: 'center',
marginBottom: '10px'
},
downloadVersionBtn: {
padding: '10px 20px',
background: '#27ae60',
color: 'white',
border: 'none',
borderRadius: '6px',
cursor: 'pointer',
marginTop: '10px'
},
gitLogList: {
display: 'flex',
flexDirection: 'column',
gap: '16px'
},
gitLogItem: {
border: '1px solid #e1e8ed',
borderRadius: '8px',
padding: '16px',
background: 'white'
},
gitLogHeader: {
display: 'flex',
justifyContent: 'space-between',
gap: '16px'
},
gitLogMeta: {
textAlign: 'right',
fontSize: '12px',
color: '#666'
},
gitLogHash: {
fontFamily: 'Consolas, monospace',
color: '#999'
},
gitLogBody: {
marginTop: '6px',
fontSize: '14px',
color: '#555',
whiteSpace: 'pre-wrap'
},
gitLogFiles: {
marginTop: '10px',
paddingTop: '10px',
borderTop: '1px solid #e1e8ed',
display: 'flex',
flexDirection: 'column',
gap: '6px',
fontSize: '13px'
},
gitLogFile: {
display: 'flex',
gap: '8px'
},
gitLogStatus: {
minWidth: '32px',
fontWeight: 'bold',
color: '#667eea'
},
purchaseCard: {
width: '330px',
padding: '18px',
background: '#fff',
border: '1px solid #dbe3ef',
borderRadius: '12px',
boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)'
},
priceTag: {
fontSize: '28px',
fontWeight: '800',
color: '#1f2937',
marginBottom: '4px'
},
purchaseProvider: {
fontSize: '14px',
color: '#52606d',
marginBottom: '14px'
},
acceptanceLabel: {
display: 'flex',
gap: '8px',
alignItems: 'flex-start',
fontWeight: '600',
fontSize: '13px',
marginTop: '10px'
},
agreementText: {
maxHeight: '90px',
overflowY: 'auto',
padding: '9px',
margin: '7px 0 10px',
background: '#f7f8fa',
borderRadius: '6px',
whiteSpace: 'pre-wrap',
fontSize: '12px',
color: '#4b5563'
},
checkoutMessage: {
marginTop: '10px',
padding: '10px',
background: '#eef6ff',
borderRadius: '6px',
fontSize: '13px',
color: '#1e4f8a'
},
paymentInstructions: {
display: 'grid',
gap: '9px',
padding: '12px',
marginTop: '10px',
background: '#f0fdf4',
border: '1px solid #bbf7d0',
borderRadius: '7px',
fontSize: '13px'
},
paymentProgress: {
display: 'grid',
gap: '7px',
padding: '10px',
background: '#eff6ff',
border: '1px solid #bfdbfe',
borderRadius: '7px',
color: '#1e3a5f'
},
paymentProgressError: {
background: '#fff1f2',
borderColor: '#fecdd3',
color: '#9f1239'
},
paymentProgressHeader: {
display: 'flex',
justifyContent: 'space-between',
gap: '12px'
},
paymentProgressTrack: {
height: '7px',
overflow: 'hidden',
background: '#dbeafe',
borderRadius: '999px'
},
paymentProgressFill: {
height: '100%',
background: '#2563eb',
borderRadius: '999px',
transition: 'width 300ms ease'
},
paymentProgressFillError: {
background: '#e11d48'
},
paymentProgressDetail: {
fontSize: '12px',
lineHeight: '1.4'
},
paymentProgressSteps: {
fontSize: '11px',
color: '#52657a'
},
paymentQrPanel: {
display: 'flex',
flexDirection: 'column',
alignItems: 'center',
gap: '8px',
padding: '12px',
background: '#ffffff',
border: '1px solid #d1fae5',
borderRadius: '7px'
},
paymentQrCaption: {
fontSize: '12px',
color: '#4b5563',
textAlign: 'center'
},
paymentAddress: {
display: 'block',
padding: '9px',
background: '#ffffff',
border: '1px solid #d1d5db',
borderRadius: '5px',
overflowWrap: 'anywhere',
userSelect: 'all'
},
cancelPaymentBtn: {
padding: '10px 14px',
background: '#ffffff',
color: '#b42318',
border: '1px solid #fda29b',
borderRadius: '6px',
cursor: 'pointer',
fontWeight: '700'
},
loading: {
textAlign: 'center',
padding: '100px 20px',
fontSize: '20px',
color: '#666'
},
error: {
textAlign: 'center',
padding: '100px 20px',
fontSize: '20px',
color: '#e74c3c'
},
pendingBanner: {
maxWidth: '1400px',
margin: '20px auto 0',
padding: '14px 24px',
background: '#fee2e2',
border: '1px solid #fecaca',
borderRadius: '10px',
color: '#991b1b',
fontWeight: '700',
fontSize: '18px',
textTransform: 'uppercase',
letterSpacing: '0.08em',
textAlign: 'center'
},
matchList: {
marginTop: '20px',
display: 'flex',
flexDirection: 'column',
gap: '10px'
},
matchBtn: {
padding: '12px 16px',
background: '#ffffff',
border: '1px solid #e1e8ed',
borderRadius: '8px',
cursor: 'pointer',
textAlign: 'left',
fontSize: '15px'
}
};

export default PluginDetailPage;
